import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const fieldBase =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all duration-150 placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60'

const fieldError =
  'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/25'

export type InputProps = Omit<ComponentPropsWithoutRef<'input'>, 'id'> & {
  id?: string
  label?: ReactNode
  error?: string | null
  inputClassName?: string
  endAdornment?: ReactNode
}

export function Input({
  id,
  label,
  error = null,
  className = '',
  inputClassName = '',
  disabled = false,
  endAdornment = null,
  ...props
}: InputProps) {
  const paddingForAdornment = endAdornment ? 'pr-11' : ''
  const input = (
    <input
      id={id}
      disabled={disabled}
      className={cn(fieldBase, error && fieldError, paddingForAdornment, inputClassName)}
      {...props}
    />
  )

  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={id}
          className={cn(
            'mb-2 block text-sm font-medium',
            disabled ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {label}
        </label>
      ) : null}
      {endAdornment ? (
        <div className="relative">
          {input}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">{endAdornment}</div>
        </div>
      ) : (
        input
      )}
      {error ? <p className="mt-2 text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  )
}
